import React, { useState, useEffect } from 'react';
import { 
  ExternalLink, Store, TrendingUp, ShoppingCart, 
  XCircle, CheckCircle2, Clock, Package 
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DashboardHome = () => {
  const [data, setData] = useState({
    user: null,
    orders: [],
    productsCount: 0,
    loading: true
  });

 useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [userRes, ordersRes, productsRes] = await Promise.all([
          api.get('/auth/user'),
          api.get('/orders/seller'),    // Ye sahi hai
          api.get('/products/my-products') // Isko '/seller' se badal kar '/my-products' kar dein
        ]);

        setData({
          user: userRes.data,
          orders: ordersRes.data,
          productsCount: productsRes.data.length,
          loading: false
        });
      } catch (err) {
        console.error("Data fetch error:", err);
        // Agar 404 aaye toh iska matlab route abhi bhi match nahi ho raha
        toast.error("Dashboard data load nahi ho saka. Route check karein.");
        setData(prev => ({ ...prev, loading: false }));
      }
    };
    fetchDashboardData();
  }, []);

  // --- STATS CALCULATIONS ---
  const deliveredOrders = data.orders.filter(o => o.status === 'delivered');
  const totalEarnings = deliveredOrders.reduce((acc, curr) => acc + curr.totalAmount, 0);
  const pendingCount = data.orders.filter(o => o.status === 'pending').length;
  const cancelledCount = data.orders.filter(o => o.status === 'cancelled').length;

  const openStore = () => {
    if (data.user?.shopName) {
      window.open(`/shop/${data.user.shopName}`, '_blank');
    }
  };

  if (data.loading) return (
    <div className="h-96 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
    </div>
  );

  return (
    <div className="max-w-6xl space-y-10 animate-in fade-in duration-500">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">
            Welcome back, {data.user?.name.split(' ')[0]}!
          </h1>
          <p className="text-gray-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-2">
            Welcome to your dashboard
          </p>
        </div>
        
        <button 
          onClick={openStore}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-100 rounded-2xl font-black text-xs uppercase tracking-widest shadow-sm hover:shadow-md transition-all active:scale-95"
        >
          <Store size={16} /> Open Store
        </button>
      </div>

      {/* --- BIG SALES CARD --- */}
      <div className="bg-black rounded-[3rem] p-10 text-white relative overflow-hidden shadow-2xl shadow-gray-200">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div>
            <p className="text-gray-400 font-black uppercase text-[11px] tracking-[0.3em] mb-2">Total Earnings (Delivered Only)</p>
            <h2 className="text-6xl font-black tracking-tighter">Rs. {totalEarnings.toLocaleString()}</h2>
            <div className="flex items-center gap-2 mt-4 text-green-400 font-bold bg-green-400/10 w-max px-3 py-1 rounded-full text-xs">
              <TrendingUp size={14} /> Total Delivered Orders: {deliveredOrders.length}
            </div>
          </div>
          
          <div className="w-full md:w-px h-px md:h-20 bg-gray-800" />

          <div className="grid grid-cols-2 gap-8 w-full md:w-auto">
            <div>
              <p className="text-gray-400 font-black uppercase text-[10px] tracking-widest mb-1">Items in Shop</p>
              <p className="text-3xl font-black">{data.productsCount}</p>
            </div>
            <div>
              <p className="text-gray-400 font-black uppercase text-[10px] tracking-widest mb-1">Total Orders</p>
              <p className="text-3xl font-black">{data.orders.length}</p>
            </div>
          </div>
        </div>

        {/* Background decoration icon */}
        <ShoppingCart size={200} className="absolute -right-10 -bottom-10 text-white/5 rotate-12" />
      </div>

      {/* --- ORDER STATUS GRID --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatusCard 
          label="Pending Orders" 
          value={pendingCount} 
          icon={<Clock size={24} />} 
          color="orange"
          desc="Action Required"
        />
        <StatusCard 
          label="Delivered" 
          value={deliveredOrders.length} 
          icon={<CheckCircle2 size={24} />} 
          color="green"
          desc="Revenue Generated"
        />
        <StatusCard 
          label="Cancelled" 
          value={cancelledCount} 
          icon={<XCircle size={24} />} 
          color="red"
          desc="Lost Opportunities"
        />
      </div>

      {/* --- QUICK LINKS / RECENT --- */}
      <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-sm">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl font-black text-gray-900">Recent Activity</h3>
          <Package className="text-gray-200" size={24} />
        </div>
        
        {data.orders.length > 0 ? (
          <div className="space-y-4">
            {data.orders.slice(0, 3).map((order) => (
              <div key={order._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-50">
                <div className="flex items-center gap-4">
                   <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${
                     order.status === 'delivered' ? 'bg-green-100 text-green-600' : 
                     order.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                   }`}>
                     {order.status === 'delivered' ? <CheckCircle2 size={16}/> : <Clock size={16}/>}
                   </div>
                   <div>
                     <p className="text-sm font-black text-gray-900 uppercase">#{order._id.slice(-6)}</p>
                     <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{order.items.length} Items • Rs.{order.totalAmount}</p>
                   </div>
                </div>
                <span className="text-[10px] font-black uppercase text-gray-400">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-10 text-gray-400 font-bold uppercase text-xs tracking-widest">No orders yet</p>
        )}
      </div>
    </div>
  );
};

// --- Reusable Component for Status Cards ---
const StatusCard = ({ label, value, icon, color, desc }) => {
  const colors = {
    orange: "bg-orange-50 text-orange-600 border-orange-100",
    green: "bg-green-50 text-green-600 border-green-100",
    red: "bg-red-50 text-red-600 border-red-100"
  };

  return (
    <div className={`p-8 rounded-[2.5rem] border shadow-sm transition-all hover:shadow-md ${colors[color]}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 bg-white rounded-xl shadow-sm">{icon}</div>
        <p className="text-3xl font-black">{value}</p>
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
        <p className="text-[11px] font-bold mt-1">{desc}</p>
      </div>
    </div>
  );
};

export default DashboardHome;